/**
 * useJabatan Hook
 * 
 * Custom hook for fetching and managing jabatan data.
 */

import * as React from "react";
import { managementService, type Jabatan, type JabatanLevel, type CreateJabatanInput, type UpdateJabatanInput } from "@/services/managementService";

export function useJabatan(level?: JabatanLevel) {
  const [jabatan, setJabatan] = React.useState<Jabatan[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const fetchJabatan = React.useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await managementService.getAllJabatan(level);
      setJabatan(data);
    } catch (err) {
      console.error("Error fetching jabatan:", err);
      setError(err instanceof Error ? err.message : "Gagal memuat data jabatan");
      setJabatan([]);
    } finally {
      setLoading(false);
    }
  }, [level]);

  React.useEffect(() => {
    fetchJabatan();
  }, [fetchJabatan]);

  return {
    jabatan,
    loading,
    error,
    fetchJabatan,
  };
}

export function useCreateJabatan() {
  const [creating, setCreating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const createJabatan = React.useCallback(async (input: CreateJabatanInput) => {
    setCreating(true);
    setError(null);

    try {
      const jabatan = await managementService.createJabatan(input);
      return jabatan;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Gagal membuat jabatan";
      setError(errorMessage);
      throw err;
    } finally {
      setCreating(false);
    }
  }, []);

  return {
    creating,
    error,
    createJabatan,
  };
}

export function useUpdateJabatan() {
  const [updating, setUpdating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const updateJabatan = React.useCallback(async (id: string, input: UpdateJabatanInput) => {
    setUpdating(true);
    setError(null);

    try {
      const jabatan = await managementService.updateJabatan(id, input);
      return jabatan;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Gagal mengupdate jabatan";
      setError(errorMessage);
      throw err;
    } finally {
      setUpdating(false);
    }
  }, []);

  return {
    updating,
    error,
    updateJabatan,
  };
}

export function useDeleteJabatan() {
  const [deleting, setDeleting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const deleteJabatan = React.useCallback(async (id: string) => {
    setDeleting(true);
    setError(null);

    try {
      const jabatan = await managementService.deleteJabatan(id);
      return jabatan;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Gagal menghapus jabatan";
      setError(errorMessage);
      throw err;
    } finally {
      setDeleting(false);
    }
  }, []);

  return {
    deleting,
    error,
    deleteJabatan,
  };
}

